import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { PRODUCTS } from "@/data/products";
import { PrivatePresentationCTA } from "@/components/PrivatePresentationCTA";
import { useLang, local } from "@/i18n/LanguageContext";

const featuredIdx = [0, 2, 4]; // Rosa, Lumumba, Quincy

const Home = () => {
  const { lang, t } = useLang();
  const home = t.home;
  const featured = featuredIdx.map((i) => PRODUCTS[i]);

  return (
    <div data-testid="home-page">
      {/* HERO ----------------------------------------------------------- */}
      <section className="relative overflow-hidden">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-32 pb-24 md:pb-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-end">
            <div className="lg:col-span-6 reveal">
              <div className="flex items-baseline gap-6 mb-12">
                <span className="font-eyebrow text-[hsl(var(--md-stone))]">{home.eyebrowYear}</span>
                <span className="hairline flex-1 max-w-[120px] mt-3" />
                <span className="font-eyebrow text-[hsl(var(--md-stone))]">{home.eyebrowEdition}</span>
              </div>
              <h1 className="display-hero text-[hsl(var(--md-ink))]">
                {home.title}
              </h1>
              <p className="font-display text-[1.5rem] md:text-[1.85rem] mt-8 text-[hsl(var(--md-ink))] max-w-[28ch] leading-tight">
                {home.tagline}
              </p>
              <p className="editorial-muted mt-10 max-w-[52ch]">
                {home.intro}
              </p>
              <div className="mt-14 flex flex-col sm:flex-row gap-5 sm:gap-10 items-start">
                <Link to="/collection" className="btn-primary" data-testid="hero-cta-collection">
                  {home.ctaCollection}
                </Link>
                <Link to="/contact" className="btn-ghost" data-testid="hero-cta-contact">
                  {home.ctaContact}
                  <ArrowUpRight size={14} strokeWidth={1.25} />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6 reveal reveal-d2">
              <div className="relative aspect-[4/5] lg:aspect-[3/4] bg-[hsl(var(--md-bone))] border border-[hsl(var(--md-line))]">
                <img
                  src={PRODUCTS[1].image}
                  alt="Daisuki — Maison Dodji"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 flex items-end justify-between bg-gradient-to-t from-[hsl(var(--md-bone))]/90 to-transparent pt-24">
                  <div>
                    <p className="font-eyebrow text-[hsl(var(--md-stone))]">{home.featureEyebrow}</p>
                    <p className="font-display text-[1.6rem] text-[hsl(var(--md-ink))] mt-1">Daisuki</p>
                  </div>
                  <Link
                    to="/collection/daisuki"
                    className="font-eyebrow text-[hsl(var(--md-ink))] flex items-center gap-2 hover:gap-3 transition-all"
                    data-testid="hero-feature-daisuki"
                  >
                    {home.featureCta} <ArrowRight size={14} strokeWidth={1.2} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAISON STATEMENT ----------------------------------------------- */}
      <section className="py-28 md:py-44 border-t border-[hsl(var(--md-line))]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-3">
              <p className="font-eyebrow text-[hsl(var(--md-stone))] sticky top-32">
                {home.statementEyebrow}
              </p>
            </div>
            <div className="md:col-span-8">
              <h2 className="display-1 text-[hsl(var(--md-ink))] max-w-[22ch]">
                {home.statementTitle}
              </h2>
              <p className="editorial mt-10 max-w-[58ch]">
                {home.statementBody}
              </p>
              <Link to="/maison" className="btn-ghost mt-12 inline-flex" data-testid="home-link-maison">
                {home.statementCta}
                <ArrowUpRight size={14} strokeWidth={1.25} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* COLLECTION OVERVIEW ------------------------------------------- */}
      <section className="bg-[hsl(var(--md-bone))] border-y border-[hsl(var(--md-line))] py-28 md:py-40 relative overflow-hidden">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-12">
              <p className="font-eyebrow text-[hsl(var(--md-stone))]">
                {home.collectionEyebrow}
              </p>
            </div>
            <div className="md:col-span-7 mt-6">
              <h2 className="display-1 text-[hsl(var(--md-ink))]">
                {home.collectionTitleLine1}<br />
                {home.collectionTitleLine2}<br />
                <span className="text-[hsl(var(--md-stone))]">
                  {home.collectionTitleLine3}
                </span>
              </h2>
            </div>
            <div className="md:col-span-4 md:col-start-9 mt-6 flex md:items-end">
              <p className="editorial-muted">
                {home.collectionAside}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PIECES ----------------------------------------------- */}
      <section className="py-28 md:py-40" data-testid="home-featured">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-end justify-between mb-20 md:mb-28">
            <p className="font-eyebrow text-[hsl(var(--md-stone))]">{home.featuredEyebrow}</p>
            <div className="hidden md:block hairline flex-1 mx-12" />
            <Link to="/collection" className="font-eyebrow text-[hsl(var(--md-ink))] hover:text-[hsl(var(--md-stone))] transition-colors flex items-center gap-3" data-testid="home-link-featured-all">
              {home.featuredAll}
              <ArrowRight size={14} strokeWidth={1.2} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-8 lg:gap-14">
            {featured.map((p, i) => (
              <Link
                to={`/collection/${p.slug}`}
                key={p.slug}
                data-testid={`featured-${p.slug}`}
                className={`group block ${i === 1 ? "md:translate-y-20" : ""}`}
              >
                <div className="relative aspect-[4/5] bg-[hsl(var(--md-bone))] border border-[hsl(var(--md-line))] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  <span className="absolute top-5 left-5 font-eyebrow text-[hsl(var(--md-stone))]">
                    N°&nbsp;{String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="mt-8">
                  <p className="font-eyebrow text-[hsl(var(--md-stone))]">
                    {local(p.capacityLabel, lang)}
                  </p>
                  <h3 className="font-display text-[1.95rem] mt-3 text-[hsl(var(--md-ink))] leading-tight">
                    {p.name}
                  </h3>
                  <p className="font-display italic text-[1.1rem] mt-2 text-[hsl(var(--md-stone))] leading-snug">
                    {local(p.tagline, lang)}
                  </p>

                  <div className="hairline mt-7" />

                  <div className="mt-5 flex items-baseline justify-between">
                    <span className="font-display text-[1.4rem] text-[hsl(var(--md-ink))]">
                      {local(p.priceLabel, lang)}
                    </span>
                    <span className="font-eyebrow text-[hsl(var(--md-ink))] flex items-center gap-2 group-hover:gap-3 transition-all">
                      {home.featuredCta}
                      <ArrowRight size={13} strokeWidth={1.2} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MAISON UNIVERSE ----------------------------------------------- */}
      <section className="bg-[hsl(var(--md-sand))]/40 border-y border-[hsl(var(--md-line))] py-28 md:py-44 relative overflow-hidden">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-3">
              <p className="font-eyebrow text-[hsl(var(--md-stone))]">{home.universeEyebrow}</p>
            </div>
            <div className="md:col-span-9">
              <h2 className="display-1 text-[hsl(var(--md-ink))] max-w-[22ch]">
                {home.universeTitle}
              </h2>

              <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-14 gap-x-12 max-w-4xl">
                {home.universePillars.map(([label, line], i) => (
                  <div key={label} className="flex gap-6">
                    <span className="font-display font-light text-[1rem] text-[hsl(var(--md-stone))] pt-2 min-w-[36px]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <p className="font-display text-[1.7rem] text-[hsl(var(--md-ink))] leading-tight">
                        {label}
                      </p>
                      <p className="editorial-muted mt-3 text-[0.98rem]">{line}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/univers" className="btn-ghost mt-20 inline-flex" data-testid="home-link-univers">
                {home.universeCta}
                <ArrowUpRight size={14} strokeWidth={1.25} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* LEGITIMACY ----------------------------------------------------- */}
      <section className="py-28 md:py-40">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-3">
              <p className="font-eyebrow text-[hsl(var(--md-stone))]">{home.placesEyebrow}</p>
            </div>
            <div className="md:col-span-9">
              <h2 className="display-2 text-[hsl(var(--md-ink))] max-w-[28ch]">
                {home.placesTitle}
              </h2>
              <div className="hairline mt-16" />
              <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
                {home.placesBlocks.map((b) => (
                  <div key={b.eyebrow}>
                    <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-3">{b.eyebrow}</p>
                    <p className="editorial">{b.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <PrivatePresentationCTA />
    </div>
  );
};

export default Home;
